import { h, Component, Host, State, Element, Prop, Watch, Listen } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  TooltipPlacement,
  TooltipVisibility,
  TooltipSchemeDefinition,
  TooltipSchemeName,
  TooltipContentAlignment,
  TooltipSize
} from "./constants/tooltip-types";
import {
  TOOLTIP_DEFAULT,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_PLACEMENT_RIGHT,
  TOOLTIP_SCHEME_CONFIG,
  TOOLTIP_SCHEME_MAP,
  TOOLTIP_VISIBILITY_ALWAYS,
  TOOLTIP_VISIBILITY_MANUAL,
  TOOLTIP_VISIBILITY_ON_CLICK,
  TOOLTIP_VISIBILITY_ON_HOVER
} from "./constants/tooltip-constants";
import { TooltipSchemeConfiguration } from "../../constants/colors";
// import { createPopper } from "@popperjs/core";
import { createPopper } from "../../assets/@popperjs/core"; // local import because of shadowRoot arrow fix in arrow modifier
import { detectSlotChanges } from "../../utils/slot-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { TOOLTIP_DEFAULT_CONTENT_ALIGNMENT } from "../../constants/global-constants";
import { isMobile } from "../../utils/screen-helper";
import { isTouchDevice } from "../../utils/touch-helper";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-tooltip",
  styleUrl: "tooltip.scss",
  shadow: true
})
export class Tooltip implements ColorableComponent<TooltipSchemeConfiguration<TooltipSchemeDefinition>> {
  schemeConfig: TooltipSchemeConfiguration<TooltipSchemeDefinition> = TOOLTIP_SCHEME_CONFIG;
  schemeMap = TOOLTIP_SCHEME_MAP;

  private internalConnectedElement;
  arrow;
  popper;
  mouseLeaveTimeout;
  parentMouseLeaveEventListenerFunction = this.parentMouseLeaveFn.bind(this);
  parentMouseLeaveEnterListenerFunction = this.parentMouseEnterFn.bind(this);
  parentClickEventListenerFunction = this.parentClickFn.bind(this);
  isFirstLoad: boolean = true;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: TooltipSchemeDefinition;

  @State() adjustedPlacement: TooltipPlacement = TOOLTIP_PLACEMENT_RIGHT;

  @Element() hostElement: HTMLEonUiTooltipElement;
  componentWrapper!: HTMLElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: TooltipSchemeName;

  /**
   * If tooltip should be connected to another element than the parent, you can set it here.
   */
  @Prop() optimizedPlacement: boolean = true;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * content alignment of slottet components.
   * @example left | center | right
   */
  @Prop({ reflect: true }) contentAlignment: TooltipContentAlignment = TOOLTIP_DEFAULT_CONTENT_ALIGNMENT;

  /**
   * The placement type of the tooltip
   */
  @Prop({ reflect: true }) placement: TooltipPlacement = TOOLTIP_PLACEMENT_RIGHT;

  /**
   * The visibility type of the tooltip. If mode is manual the developer has to handle the visibility via visible attribute.
   */
  @Prop({ reflect: true, mutable: true }) visibility: TooltipVisibility = TOOLTIP_VISIBILITY_ON_HOVER;

  /**
   * Is the tooltip visible or not?
   */
  @Prop({ mutable: true }) visible: boolean = false;

  /**
   * Set the size of tooltip
   */
  @Prop({ mutable: true }) size: TooltipSize = TOOLTIP_DEFAULT;

  /**
   * Set the time delay off if not required
   */
  @Prop({ mutable: true }) noTransitionDelay: boolean = false;

  /**
   * If tooltip should be connected to another element than the parent, you can set it here.
   */
  @Prop() connectedElement: HTMLElement;

  componentWillLoad() {
    this.applyScheme();
    this.internalConnectedElement = this.connectedElement ? this.connectedElement : this.hostElement.parentElement;
    // aria-describedby="tooltip" ADD TO PARENT with unique id?
  }

  componentDidLoad() {
    this.setCssVariables();
    this.initPopper();
    this.activateTooltipVisibility();
    this.updatePlacement();
    this.applySize();

    detectSlotChanges(() => {
      this.updatePlacement();
      if (this.popper) this.updatePlacement();
    }, this.hostElement);
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("connectedElement")
  updateConnectedElement() {
    this.internalConnectedElement = this.connectedElement ? this.connectedElement : this.hostElement.parentElement;
    this.popper.destroy();
    this.initPopper();
  }

  @Watch("placement")
  updatePlacement() {
    this.adjustedPlacement = isMobile() ? TOOLTIP_PLACEMENT_BOTTOM : this.placement;
    this.popper && this.popper.setOptions({ placement: this.adjustedPlacement });
  }

  @Watch("visible")
  handleInitialPositioning() {
    // reposition with first load to ensure exact position.
    if (this.isFirstLoad) {
      this.isFirstLoad = false;
      this.updatePlacement();
    }
  }

  @Watch("size")
  applySize() {
    this.updatePlacement();
  }

  @Watch("visibility")
  updateVisiblity() {
    this.activateTooltipVisibility();
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.updatePlacement();
  }

  private initPopper() {
    this.popper = createPopper(this.internalConnectedElement, this.hostElement, {
      placement: this.adjustedPlacement,
      strategy: "absolute",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8]
          }
        },
        {
          name: "preventOverflow",
          options: {
            padding: 12, // 12 instead of 16 to have a little overlap
            mainAxis: true, // true by default
            altAxis: false // false by default
          }
        },
        {
          name: "arrow",
          options: {
            element: this.arrow,
            padding: 16
          }
        },
        {
          name: "applyArrowHide",
          enabled: true,
          phase: "write",
          fn({ state }) {
            const { arrow } = state.elements;
            if (arrow) {
              const isSmall = arrow.classList.contains("eonui-small-size");
              if (!isSmall && state.modifiersData.arrow.centerOffset !== 0) {
                arrow.setAttribute("data-hide", "");
              } else {
                arrow.removeAttribute("data-hide");
              }
            }
          }
        }
      ]
    });
  }

  private activateTooltipVisibility() {
    this.removeEventListeners();
    if (isTouchDevice() && this.visibility === TOOLTIP_VISIBILITY_ON_HOVER) {
      this.visibility = TOOLTIP_VISIBILITY_ON_CLICK;
      this.internalConnectedElement.addEventListener("mouseleave", this.parentMouseLeaveEventListenerFunction);
    }
    switch (this.visibility) {
      case TOOLTIP_VISIBILITY_ON_HOVER:
        this.visible = false;
        this.attachOpenOnHoverFunctionalityToParent();
        break;
      case TOOLTIP_VISIBILITY_ON_CLICK:
        this.visible = false;
        this.attachOpenOnClickFunctionalityToParent();
        break;
      case TOOLTIP_VISIBILITY_ALWAYS:
        // wait a bit on first rendering to ensure correct position.
        if (this.isFirstLoad) {
          setTimeout(() => {
            this.visible = true;
          }, 1000);
        } else {
          this.visible = true;
        }
        break;
      case TOOLTIP_VISIBILITY_MANUAL:
        this.visible = false;
        break;
    }
  }

  /*
   * Closes tooltip after mouse leave at the bottom (400ms)
   */
  private parentMouseLeaveFn() {
    if (!this.noTransitionDelay) {
      this.mouseLeaveTimeout = setTimeout(() => {
        this.visible = false;
      }, 400);
    } else {
      this.visible = false;
    }
  }

  /*
   * Interrupts mouse leave function if reentering parent or tooltip (400ms)
   */
  private parentMouseEnterFn() {
    this.visible = true;
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
    }
  }

  /*
   * Interrupts mouse leave function if reentering parent or tooltip (400ms)
   */
  private parentClickFn(e) {
    this.hostElement.addEventListener("mouseleave", this.parentMouseLeaveEventListenerFunction);
    this.hostElement.addEventListener("mouseenter", this.parentMouseLeaveEnterListenerFunction);

    const target = e.target as HTMLElement;

    if (this.visible) {
      if (target !== this.hostElement && !this.hostElement.contains(target)) {
        this.visible = false;
      }
    } else {
      this.visible = true;
    }
  }

  private attachOpenOnHoverFunctionalityToParent() {
    this.internalConnectedElement.addEventListener("mouseleave", this.parentMouseLeaveEventListenerFunction);
    this.internalConnectedElement.addEventListener("mouseenter", this.parentMouseLeaveEnterListenerFunction);
  }

  private attachOpenOnClickFunctionalityToParent() {
    this.internalConnectedElement.addEventListener("click", this.parentClickEventListenerFunction);
  }

  private removeEventListeners() {
    this.internalConnectedElement.removeEventListener("mouseleave", this.parentMouseLeaveEventListenerFunction);
    this.internalConnectedElement.removeEventListener("mouseenter", this.parentMouseLeaveEnterListenerFunction);
    this.hostElement.removeEventListener("mouseleave", this.parentMouseLeaveEventListenerFunction);
    this.hostElement.removeEventListener("mouseenter", this.parentMouseLeaveEnterListenerFunction);
    this.internalConnectedElement.removeEventListener("click", this.parentClickEventListenerFunction);
  }

  render() {
    return (
      <Host
        id="unique"
        role="tooltip"
        class={pfx({
          visible: this.visible,
          "apply-transition": !this.noTransitionDelay
        })}
      >
        <div class={pfx({ wrapper: true, visible: this.visible, "small-size": this.size === "small" })}>
          <div class={pfx({ inner: true, [`alignment-${this.contentAlignment}`]: this.contentAlignment })}>
            <slot />
          </div>
          <div
            id="arrow"
            class={pfx({ "apply-transition": !this.noTransitionDelay, "small-size": this.size === "small" })}
            ref={(el) => (this.arrow = el as HTMLElement)}
          ></div>
        </div>
      </Host>
    );
  }
}
